import React from "react";
import { useTranslation } from "react-i18next";
import {
  useTable,
  useSortBy,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  Row,
  Cell,
} from "react-table";
import { Button } from "@mantine/core";
import { ArrowIcon } from "../../assets/svgs";

type Props = {
  isPagination?: boolean;
  data: any;
  columns: any;
  nextToken: string | null;
  loadOrders?: () => void;
  fetchLoading: boolean;
};

const Table: React.FC<Props> = ({
  isPagination = true,
  data,
  columns,
  nextToken,
  loadOrders,
  fetchLoading,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    gotoPage,
    pageOptions,
    state,
    prepareRow,
  }: any = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 16 } as any,
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect
  );

  const { pageIndex } = state;

  const { t } = useTranslation();

  return (
    <>
      <div className="table-wrapper table-auto">
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup: any) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: any) => (
                  <th
                    {...column.getHeaderProps({
                      ...column.getSortByToggleProps(),
                      style: {
                        minWidth: column.minWidth,
                      },
                    })}
                  >
                    {column.render("Header")}
                    <span style={{ marginLeft: 7 }}>
                      <span
                        style={{
                          display: "inline-block",
                          transition: "0.2s transform ease-in-out",
                          transform: column.isSortedDesc
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                        }}
                      >
                        <ArrowIcon hide={!column.isSorted} strokeWidth={2} />
                      </span>
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row: Row) => {
              prepareRow(row);

              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell: Cell) => {
                    return (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {isPagination && (
        <div className="table-pagination">
          <div className="table-pagination__button-group">
            {pageOptions.map((_: any, index: number) => {
              return (
                <Button
                  key={index}
                  onClick={() => gotoPage(index)}
                  disabled={index === pageIndex}
                  style={{
                    paddingLeft: 18,
                    paddingRight: 18,
                    cursor: index === pageIndex ? "default" : "pointer",
                  }}
                  type="button"
                >
                  {index + 1}
                </Button>
              );
            })}
            {nextToken && loadOrders && (
              <Button
                px={18}
                ml={10}
                type="button"
                onClick={loadOrders}
                disabled={fetchLoading || !nextToken}
                loading={fetchLoading}
              >
                {t("loadMore")}
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Table;
